import React, { useCallback } from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import { NewsItem } from '@components/posts';
import type { Post } from '../../types';

/**
 * Per-row wrapper that binds the postId to parent-supplied handlers so the
 * callbacks passed to the entry are stable per item. This lets React.memo on
 * NewsItem skip re-renders when unrelated posts change.
 *
 * Renders NewsItem (text-first noticeboard entry) since the feed went
 * news-only: photo-led PostCard made photo-less news read as empty boxes.
 * The feed-card testID + data-post-id hooks stay — the audits key on them.
 */
export const FeedPostItem = React.memo(function FeedPostItem({
  post,
  isLiked,
  containerStyle,
  currentUserId,
  onOpen,
  onLike,
  onOpenComments,
  onEdit,
  onDelete,
  animateIn,
}: {
  post: Post;
  isLiked: boolean;
  containerStyle: StyleProp<ViewStyle>;
  currentUserId?: string;
  onOpen: (postId: string) => void;
  onLike: (postId: string) => void;
  onOpenComments: (postId: string) => void;
  onEdit: (postId: string) => void;
  onDelete: (postId: string) => void;
  /** Web-only CSS enter animation for a row just inserted above the rest of
   *  the feed (useNewItemIds.ts) — ignored on native (dataSet is a no-op
   *  there). */
  animateIn?: boolean;
}) {
  const handleOpen = useCallback(() => onOpen(post.id), [onOpen, post.id]);
  const handleLike = useCallback(() => onLike(post.id), [onLike, post.id]);
  const handleComment = useCallback(() => onOpenComments(post.id), [onOpenComments, post.id]);
  const handleEdit = useCallback(() => onEdit(post.id), [onEdit, post.id]);
  const handleDelete = useCallback(() => onDelete(post.id), [onDelete, post.id]);

  return (
    <View
      style={containerStyle}
      testID="feed-card"
      // @ts-expect-error dataSet is a react-native-web prop (postId is the
      // visual-audit CSS masking hook; rediAnim drives the list-item-in
      // keyframe in motion.ts when this row was just inserted above the
      // rest of the feed); not in RN's type defs
      dataSet={animateIn ? { postId: post.id, rediAnim: 'list-item-in' } : { postId: post.id }}>
      <NewsItem
        post={post}
        isLiked={isLiked}
        currentUserId={currentUserId}
        onPress={handleOpen}
        onLike={handleLike}
        onComment={handleComment}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </View>
  );
});
